import React from 'react';
import {
  datetime,
  toHumanDateTimeWithTZ,
  toUSFormat,
  type DatetimeType,
} from '@common/services/datetime';
import { IncentiveRatesView } from '@scheduling/components';
import { Gutter, Icon, Text, Title, Col, Row } from '@common/components';
import { useCurrentMembership } from '@identity/hooks';
import { UnitRateType } from '@scheduling/graphql';

function DynamicRateDetails({ unitRates }: { unitRates: UnitRateType }) {
  return (
    <>
      <Col span={24}>
        <Gutter size="md" direction="vertical">
          <IncentiveRatesView.Dynamic rates={unitRates} />
        </Gutter>
      </Col>
      <Col span={24}>
        <IncentiveRatesView.Variable rates={unitRates} />
      </Col>
    </>
  );
}

function StaticRateDetails({ unitRates }: { unitRates: UnitRateType }) {
  if (!unitRates.staticPricingTiers?.length) return null;

  return (
    <Col span={24}>
      <IncentiveRatesView.Static
        tiers={unitRates.staticPricingTiers}
        displayAsLumpSum={unitRates.displayAsLumpSum}
      />
    </Col>
  );
}

// NOTE: `insertWorkforceGroupUnitRates` stores the end date a day past the one the user picked,
// so an inclusive range is rendered by pulling it back.
export const adjustEndDate = (
  effectiveDate: string,
  endDate: string | null
): DatetimeType =>
  datetime(effectiveDate).isSame(endDate)
    ? datetime(endDate).endOf('day')
    : datetime(endDate).subtract(1, 'days').endOf('day');

export const adjustEffectiveDate = (effectiveDate: string): DatetimeType =>
  datetime(effectiveDate).startOf('day');

export function IncentiveRatesViewDrawerUnitRatesRow({
  unitRates,
}: {
  unitRates: UnitRateType;
}) {
  const { effectiveDate, endDate, createdAt, updatedAt, createdBy } = unitRates;
  const isStatic = unitRates.pricingMode === 'STATIC';
  const currentMembership = useCurrentMembership({ hideError: true });
  const timeZone = currentMembership?.worksUser?.timeZone;

  const adjustedEndDate = adjustEndDate(effectiveDate, endDate);
  const adjustedEffectiveDate = adjustEffectiveDate(effectiveDate);

  return (
    <Row gutter={[0, 16]}>
      <Col span={24}>
        <Row gutter={[0, 8]}>
          <Col span={5}>
            <Title size="h3">
              {toUSFormat(adjustedEffectiveDate || updatedAt || createdAt)}
              <Gutter inlineBlock direction="left">
                <Icon icon="sun" color="harvestGold" size="lg" />
              </Gutter>
            </Title>
          </Col>
          {endDate && (
            <Col span={19}>
              <Title size="h3">
                {`- ${toUSFormat(adjustedEndDate)}`}
                <Gutter inlineBlock direction="left">
                  <Icon icon="moon" color="skyRegular" size="lg" />
                </Gutter>
              </Title>
            </Col>
          )}
        </Row>
      </Col>
      {isStatic ? (
        <StaticRateDetails unitRates={unitRates} />
      ) : (
        <DynamicRateDetails unitRates={unitRates} />
      )}
      {createdBy && (
        <Col span={24}>
          <Text className="d-block">
            {createdBy.name} made these changes on{' '}
            {toHumanDateTimeWithTZ(createdAt, timeZone)}.
          </Text>
        </Col>
      )}
    </Row>
  );
}
